import type { LinkPreviewResponse } from "./authApi";
import { decryptAtRest, encryptAtRest } from "./crypto";
import { getDb } from "./db";
import { getLocalCacheKey } from "./localCacheKey";

const LINK_PREVIEW_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export function normalizeLinkPreviewUrl(url: string): string {
  try {
    const u = new URL(url);
    u.hash = "";
    return u.href;
  } catch {
    return url;
  }
}

type PreviewPackedHeader = {
  meta: LinkPreviewResponse;
  imageMime: string | null;
};

function packPreview(
  data: LinkPreviewResponse,
  imageBytes: Uint8Array | null,
  imageMime: string | null,
): Uint8Array {
  const headerJson = new TextEncoder().encode(
    JSON.stringify({ meta: data, imageMime } satisfies PreviewPackedHeader),
  );
  const imgLen = imageBytes?.length ?? 0;
  const out = new Uint8Array(4 + headerJson.length + imgLen);
  new DataView(out.buffer).setUint32(0, headerJson.length, true);
  out.set(headerJson, 4);
  if (imageBytes) out.set(imageBytes, 4 + headerJson.length);
  return out;
}

function unpackPreview(buf: Uint8Array): {
  data: LinkPreviewResponse;
  imageBytes: Uint8Array | null;
  imageMime: string | null;
} {
  const dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  const headerLen = dv.getUint32(0, true);
  const headerJson = new TextDecoder().decode(buf.subarray(4, 4 + headerLen));
  const header = JSON.parse(headerJson) as PreviewPackedHeader;
  const rest = buf.subarray(4 + headerLen);
  const imageBytes = rest.length > 0 ? rest : null;
  return { data: header.meta, imageBytes, imageMime: header.imageMime };
}

export async function readLinkPreviewCached(
  pageUrl: string,
): Promise<{ data: LinkPreviewResponse; imageBytes: Uint8Array | null; imageMime: string | null } | null> {
  const key = getLocalCacheKey();
  if (!key) return null;
  const id = normalizeLinkPreviewUrl(pageUrl);
  const db = await getDb();
  const row = await db.get("link_preview_cache", id);
  if (!row) return null;
  if (Date.now() - row.fetchedAt > LINK_PREVIEW_TTL_MS) {
    await db.delete("link_preview_cache", id);
    return null;
  }
  try {
    const encrypted = new Uint8Array(row.encrypted);
    const plain = decryptAtRest(encrypted, key);
    const u = unpackPreview(plain);
    return { data: u.data, imageBytes: u.imageBytes, imageMime: u.imageMime };
  } catch {
    await db.delete("link_preview_cache", id);
    return null;
  }
}

export async function writeLinkPreviewCached(
  pageUrl: string,
  data: LinkPreviewResponse,
  imageBytes: Uint8Array | null,
  imageMime: string | null,
): Promise<void> {
  const key = getLocalCacheKey();
  if (!key) return;
  if (data.fallback) return;
  const id = normalizeLinkPreviewUrl(pageUrl);
  const packed = packPreview(data, imageBytes, imageMime);
  const encrypted = encryptAtRest(packed, key);
  const db = await getDb();
  await db.put("link_preview_cache", {
    id,
    encrypted: encrypted.slice().buffer,
    fetchedAt: Date.now(),
  });
}
